// Coordinator reachability and public configuration. Both are polled with
// backoff; components read the last known value and its freshness, so every
// screen can tell "loading", "offline" and "stale" apart.

import { create } from 'zustand';
import type { HealthInfo, PublicConfig } from '@kumio/shared';
import { api } from '../lib/api';
import { local } from '../lib/storage';

type Status = 'unknown' | 'online' | 'offline';

interface CoordState {
  status: Status;
  health: HealthInfo | null;
  config: PublicConfig | null;
  configFromCache: boolean;
  lastOk: number | null;
  lastError: string | null;
  refresh: () => Promise<void>;
}

const cachedConfig = local.get<PublicConfig | null>('config.cache', null);

export const useCoordinator = create<CoordState>((set, get) => ({
  status: 'unknown',
  health: null,
  config: cachedConfig,
  configFromCache: !!cachedConfig,
  lastOk: null,
  lastError: null,
  refresh: async () => {
    try {
      const [health, config] = await Promise.all([api<HealthInfo>('/v1/health', { timeoutMs: 6000 }), api<PublicConfig>('/v1/config', { timeoutMs: 6000 })]);
      local.set('config.cache', config);
      set({ status: 'online', health, config, configFromCache: false, lastOk: Date.now(), lastError: null });
    } catch (e) {
      set({ status: 'offline', health: null, lastError: (e as Error).message, config: get().config });
    }
  },
}));

let timer: ReturnType<typeof setTimeout> | null = null;
export function startCoordinatorPolling() {
  if (timer) return;
  const tick = async () => {
    await useCoordinator.getState().refresh();
    const delay = useCoordinator.getState().status === 'online' ? 20000 : 8000;
    timer = setTimeout(tick, document.hidden ? delay * 3 : delay);
  };
  void tick();
}

/** The label used for the token shortcut: the configured ticker, or a neutral word. */
export function tokenLabel(_config: PublicConfig | null): string {
  return 'Token';
}
